import {
  type DecisionImportIssue,
  decisionImportIssues,
  type DecisionQuestionOrigin,
  type DecisionQuestionView,
  MAX_DECISION_ASKS,
  MAX_DECISION_OPTIONS,
  UpsertDecisionQuestionInput,
} from '@apm/contracts';
import { randomUuid } from '../../lib/uuid';

/**
 * Bản nháp một câu trong trình soạn tại chỗ — APM-DSX-e (plan §11.4).
 *
 * Hàm thuần: trình soạn giữ bản nháp này trong state của thẻ, và chỉ lúc lưu
 * mới đổi thành input của lệnh `UpsertDecisionQuestion` — **một** lệnh cho cả
 * câu, validate bằng đúng schema server dùng.
 *
 * `rowId` chỉ để React giữ đúng hàng (và đúng ô đang gõ) khi thêm / xoá giữa
 * danh sách — không bao giờ gửi đi.
 */

export interface OptionDraft {
  readonly rowId: string;
  readonly key: string;
  readonly title: string;
  readonly detail: string;
  readonly recommended: boolean;
}

export interface AskDraft {
  readonly rowId: string;
  readonly text: string;
}

export interface QuestionDraft {
  readonly code: string;
  readonly title: string;
  readonly origin: DecisionQuestionOrigin | '';
  readonly sourceRef: string;
  readonly context: string;
  /** Mỗi dòng một điểm "Cần chốt"; dòng rỗng bị bỏ lúc lưu. */
  readonly asks: readonly AskDraft[];
  readonly options: readonly OptionDraft[];
  /** Cho chọn nhiều đáp án — APM-DSX-multi. Bật thì không có "nên chọn". */
  readonly multiple: boolean;
}

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVW'.split('');

/** Mã phương án kế tiếp còn trống — A…W, bỏ `X` ("Phương án khác"). */
export function nextOptionKey(used: readonly string[]): string | null {
  return LETTERS.find((letter) => !used.includes(letter)) ?? null;
}

function blankOption(key: string): OptionDraft {
  return { rowId: randomUuid(), key, title: '', detail: '', recommended: false };
}

/** Câu mới: hai phương án A, B để trống — đúng số tối thiểu server đòi. */
export function emptyDraft(code: string): QuestionDraft {
  return {
    code,
    title: '',
    origin: '',
    sourceRef: '',
    context: '',
    asks: [],
    options: [blankOption('A'), blankOption('B')],
    multiple: false,
  };
}

/**
 * Câu của server ⇒ bản nháp. Có `previous` thì giữ `rowId` cũ (phương án
 * theo mã, dòng "Cần chốt" theo thứ tự): nhận bản server mới mà đổi hết
 * `rowId` là dựng lại mọi ô, và ô đang gõ mất tiêu điểm.
 */
export function draftOf(question: DecisionQuestionView, previous?: QuestionDraft): QuestionDraft {
  const optionRow = new Map(previous?.options.map((option) => [option.key, option.rowId]));
  return {
    code: question.code,
    title: question.title,
    origin: question.origin ?? '',
    sourceRef: question.sourceRef ?? '',
    context: question.context ?? '',
    asks: question.asks.map((text, index) => ({
      rowId: previous?.asks[index]?.rowId ?? randomUuid(),
      text,
    })),
    options: question.options.map((option) => ({
      rowId: optionRow.get(option.key) ?? randomUuid(),
      key: option.key,
      title: option.title,
      detail: option.detail ?? '',
      recommended: option.recommended === true,
    })),
    multiple: question.multiple,
  };
}

/**
 * Dấu vân tay nội dung — so hai bản để biết "có gì đổi không". Bỏ `rowId`,
 * cắt khoảng trắng và dòng "Cần chốt" rỗng giống hệt lúc lưu, để gõ một dấu
 * cách rồi xoá không thành một event.
 */
export function draftSnapshot(draft: QuestionDraft): string {
  return JSON.stringify([
    draft.code.trim(),
    draft.title.trim(),
    draft.origin,
    draft.sourceRef.trim(),
    draft.context.trim(),
    draft.asks.map((ask) => ask.text.trim()).filter((text) => text !== ''),
    draft.options.map((option) => [
      option.key,
      option.title.trim(),
      option.detail.trim(),
      option.recommended,
    ]),
    draft.multiple,
  ]);
}

/**
 * Bản nháp ⇒ input của lệnh, validate bằng **đúng** schema server dùng, cộng
 * luật mã câu duy nhất trong sổ (server có `UNIQUE(sheet_id, code)`, schema
 * một câu thì không thấy các câu khác).
 *
 * `groupId`: `undefined` ⇒ không gửi, server giữ nhóm hiện tại của câu. Câu
 * đã có **luôn** đi đường này — nhóm của nó đổi bằng kéo thả (lệnh sắp lại),
 * và gửi kèm một `groupId` cũ trong thẻ là kéo câu về chỗ cũ.
 */
export function questionInputOf(
  draft: QuestionDraft,
  target: { readonly questionId?: string | undefined; readonly groupId?: string | null },
  otherCodes: readonly string[],
  duplicateMessage: (code: string) => string,
):
  | { readonly ok: true; readonly input: UpsertDecisionQuestionInput }
  | { readonly ok: false; readonly issues: DecisionImportIssue[] } {
  const result = UpsertDecisionQuestionInput.safeParse({
    ...(target.questionId ? { questionId: target.questionId } : {}),
    ...(target.groupId !== undefined ? { groupId: target.groupId } : {}),
    code: draft.code,
    title: draft.title,
    origin: draft.origin === '' ? null : draft.origin,
    sourceRef: draft.sourceRef.trim() === '' ? null : draft.sourceRef,
    context: draft.context.trim() === '' ? null : draft.context,
    asks: draft.asks.map((ask) => ask.text.trim()).filter((text) => text !== ''),
    options: draft.options.map((option) => ({
      key: option.key,
      title: option.title,
      ...(option.detail.trim() ? { detail: option.detail } : {}),
      ...(option.recommended && !draft.multiple ? { recommended: true } : {}),
    })),
    multiple: draft.multiple,
  });
  const issues = result.success ? [] : decisionImportIssues(result.error);
  const code = draft.code.trim();
  if (code !== '' && otherCodes.includes(code)) {
    issues.push({ path: 'code', message: duplicateMessage(code) });
  }
  return result.success && issues.length === 0
    ? { ok: true, input: result.data }
    : { ok: false, issues };
}

/**
 * Bật/tắt "chọn nhiều đáp án". Bật thì xoá mọi "nên chọn" — câu chọn nhiều
 * không có đề xuất (người dùng chốt 2026-10-07).
 */
export function toggleMultiple(draft: QuestionDraft): QuestionDraft {
  const multiple = !draft.multiple;
  return {
    ...draft,
    multiple,
    options: multiple
      ? draft.options.map((option) => ({ ...option, recommended: false }))
      : draft.options,
  };
}

/** "Nên chọn" tối đa một: bật một cái là tắt các cái còn lại; bấm lại cái đang bật là tắt. */
export function toggleRecommended(draft: QuestionDraft, rowId: string): QuestionDraft {
  return {
    ...draft,
    options: draft.options.map((option) =>
      option.rowId === rowId
        ? { ...option, recommended: !option.recommended }
        : { ...option, recommended: false },
    ),
  };
}

/** Thêm phương án cuối danh sách với mã kế tiếp còn trống; hết chỗ ⇒ `null`. */
export function addOption(
  draft: QuestionDraft,
): { readonly draft: QuestionDraft; readonly rowId: string } | null {
  if (draft.options.length >= MAX_DECISION_OPTIONS) return null;
  const key = nextOptionKey(draft.options.map((option) => option.key));
  if (key === null) return null;
  const option = blankOption(key);
  return { draft: { ...draft, options: [...draft.options, option] }, rowId: option.rowId };
}

/**
 * Thêm một dòng "Cần chốt" ngay **dưới** dòng `afterRowId` (`null` ⇒ cuối) —
 * Enter ở một dòng là dòng kế tiếp ở đúng chỗ ấy, như checklist của task.
 */
export function insertAsk(
  draft: QuestionDraft,
  afterRowId: string | null,
): { readonly draft: QuestionDraft; readonly rowId: string } | null {
  if (draft.asks.length >= MAX_DECISION_ASKS) return null;
  const ask: AskDraft = { rowId: randomUuid(), text: '' };
  const index =
    afterRowId === null
      ? draft.asks.length
      : draft.asks.findIndex((entry) => entry.rowId === afterRowId) + 1;
  const at = index <= 0 ? draft.asks.length : index;
  return {
    draft: { ...draft, asks: [...draft.asks.slice(0, at), ask, ...draft.asks.slice(at)] },
    rowId: ask.rowId,
  };
}

/** Bỏ một dòng "Cần chốt"; trả về dòng đứng trước nó (để đưa con trỏ về). */
export function removeAsk(
  draft: QuestionDraft,
  rowId: string,
): { readonly draft: QuestionDraft; readonly focus: string | null } {
  const index = draft.asks.findIndex((entry) => entry.rowId === rowId);
  return {
    draft: { ...draft, asks: draft.asks.filter((entry) => entry.rowId !== rowId) },
    focus: index > 0 ? (draft.asks[index - 1]?.rowId ?? null) : null,
  };
}
